import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Button,
  Modal,
  IconButton,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { gsap } from "gsap";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { masterlayoutbg } from "../../../../../assets";

/* ===============================
   FLOOR PLAN DATA (Using floor)
================================= */

const floorPlansData = [
  {
    id: 1,
    floor: "Tower A & B",
    facings: [
      {
        image: "https://dprstorage.b-cdn.net/RW/projects/cascades/A-B.png",
        downloadImage:
          "https://dprstorage.b-cdn.net/RW/projects/cascades/A-B-1536x1087.jpeg",
        details: "",
      },
    ],
  },
  {
    id: 2,
    floor: "Tower C",
    facings: [
      {
        image: "https://dprstorage.b-cdn.net/RW/projects/cascades/c.png",
        downloadImage:
          "https://dprstorage.b-cdn.net/RW/projects/cascades/c.png",
        details: "",
      },
    ],
  },
  {
    id: 3,
    floor: "Tower D",
    facings: [
      {
        image: "https://dprstorage.b-cdn.net/RW/projects/cascades/d.png",
        downloadImage:
          "https://dprstorage.b-cdn.net/RW/projects/cascades/d.png",
        details: "",
      },
    ],
  },
  {
    id: 4,
    floor: "Tower E",
    facings: [
      {
        image: "https://dprstorage.b-cdn.net/RW/projects/cascades/e.png",
        downloadImage:
          "https://dprstorage.b-cdn.net/RW/projects/cascades/e.png",
        details: "",
      },
    ],
  },
];

interface CascadesFloorPlansProps {
  title?: string;
}

const CascadesFloorPlans: React.FC<CascadesFloorPlansProps> = ({ title }) => {
  const textRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLDivElement | null>(null);

  const [selectedFloor, setSelectedFloor] = useState(floorPlansData[0]);
  const [selectedFacing, setSelectedFacing] = useState(
    floorPlansData[0].facings[0]
  );
  const [openModal, setOpenModal] = useState(false);

  const isMobile = useMediaQuery("(max-width:768px)");

  /* ===============================
     Animation
  ================================= */

  useEffect(() => {
    if (textRef.current) {
      gsap.fromTo(
        textRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }

    if (imageRef.current) {
      gsap.fromTo(
        imageRef.current,
        { opacity: 0, scale: 0.9 },
        { opacity: 1, scale: 1, duration: 1.2, delay: 0.2 }
      );
    }
  }, [selectedFacing]);

  /* ===============================
     Sync Facing When Floor Changes
  ================================= */

  useEffect(() => {
    setSelectedFacing(selectedFloor.facings[0]);
  }, [selectedFloor]);

  /* ===============================
     Download Handler
  ================================= */

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = selectedFacing.downloadImage;
    link.download = `${selectedFloor.floor}`;
    link.click();
  };

  /* ===============================
     Prev / Next Navigation
  ================================= */

  const handlePrevNext = (dir: "prev" | "next") => {
    const currentIndex = floorPlansData.findIndex(
      (plan) => plan.id === selectedFloor.id
    );

    const newIndex =
      dir === "prev"
        ? (currentIndex - 1 + floorPlansData.length) %
          floorPlansData.length
        : (currentIndex + 1) % floorPlansData.length;

    setSelectedFloor(floorPlansData[newIndex]);
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        backgroundImage: `url(${masterlayoutbg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        overflow: "hidden",
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0,0,0,0.6)",
          zIndex: 1,
        },
      }}
    >
      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 2, py: 8 }}>
        {/* Title */}
        <Box textAlign="center">
          <Typography variant="h4" fontWeight={600} color="white">
            {title || "FLOOR PLANS"}
          </Typography>
          <Divider
            sx={{
              width: { xs: "250px", md: "400px" },
              backgroundColor: "white",
              mx: "auto",
              my: 2,
            }}
          />
        </Box>

        {/* Floor Tabs */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 4,
          }}
        >
          {floorPlansData.map((plan) => (
            <Button
              key={plan.id}
              variant="contained"
              onClick={() => setSelectedFloor(plan)}
              sx={{
                background:
                  selectedFloor.id === plan.id ? "#14658B" : "transparent",
                color: "white",
                border: "1px solid white",
                borderRadius: 0,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  background: "#14658B",
                },
                width: { xs: "45%", md: "auto" },
              }}
            >
              {plan.floor}
            </Button>
          ))}
        </Box>

        {/* Image */}
        <Box
          ref={imageRef}
          sx={{ maxWidth: "750px", mx: "auto", textAlign: "center" }}
        >
          <img
            src={selectedFacing.image}
            alt={selectedFloor.floor}
            style={{
              width: "100%",
              borderRadius: 8,
              border: "1px solid white",
              padding: 5,
              boxShadow: "0 0 15px rgba(15,99,165,0.7)",
              cursor: "pointer",
            }}
            onClick={() => setOpenModal(true)}
          />
        </Box>

        {/* Modal */}
        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "100vh",
              backgroundColor: "rgba(0,0,0,0.95)",
              position: "relative",
            }}
          >
            <IconButton
              sx={{ position: "absolute", top: 20, right: 20, color: "white" }}
              onClick={() => setOpenModal(false)}
            >
              <CloseIcon sx={{ fontSize: 40 }} />
            </IconButton>

            <IconButton
              sx={{ position: "absolute", top: 20, left: 20, color: "white" }}
              onClick={handleDownload}
            >
              <DownloadIcon sx={{ fontSize: 40 }} />
            </IconButton>

            <IconButton
              sx={{
                position: "absolute",
                left: 20,
                top: "50%",
                transform: "translateY(-50%)",
                color: "white",
              }}
              onClick={() => handlePrevNext("prev")}
            >
              <ArrowBackIosIcon />
            </IconButton>

            <IconButton
              sx={{
                position: "absolute",
                right: 20,
                top: "50%",
                transform: "translateY(-50%)",
                color: "white",
              }}
              onClick={() => handlePrevNext("next")}
            >
              <ArrowForwardIosIcon />
            </IconButton>

            <Box
              component="img"
              src={selectedFacing.downloadImage}
              alt="Full Floor Plan"
              sx={{
                maxWidth: "90%",
                maxHeight: "90vh",
                objectFit: "contain",
              }}
            />
          </Box>
        </Modal>
      </Container>
    </Box>
  );
};

export default CascadesFloorPlans;